import { dayjs } from '#gp/app-common/util/dayjs';
import { gpCommon } from '../app';
import { AuthCheckResultChoices, AuthorisationCheckerService, parseJwtToken } from './authorisation-checker-service';
import { gpRenderedAuthSeedApplies } from './authorisation-seed';
import { ILogglyService } from './jsloggly-service';
import { IStorageService } from './local-storage-service';
import { tokenCookieWrites } from './token-cookie';


interface AuthData {
    email: string;
    token: string;
    userId: string;
}

export interface IAuthorisationStatus {
    loginChecking: boolean;
    email: string;
    userId: string;
    token: string;
    loggedInStatus: AuthCheckResultChoices;
    isAuthorised: boolean;
}

export interface IAuthorisationCheckerService {
    authorisationChanged(callback: AuthoisationStatusCallback): void;
    checkAuthorisationIfNeeded(): ng.IPromise<IAuthorisationStatus>;
    resetCheckedStatus(): void;
}

type AuthoisationStatusCallback = (authStatus: IAuthorisationStatus) => void

class AuthorisationCheckerWithCachingService implements IAuthorisationCheckerService {
    private DEBUG_DELAY = 0;
    private listeners: ((authStatus: IAuthorisationStatus) => void)[] = [];
    private checkAuthPromise: ng.IPromise<IAuthorisationStatus> | null = null;
    private renderedAuthSeedSpent = false;
    private lastAuthorisationStatus: IAuthorisationStatus = {
        email: "",
        token: "",
        userId: "",
        loginChecking: false,
        loggedInStatus: 'unauthorized',
        isAuthorised: false,
    };

    public static readonly serviceName = "gpAuthCheckerServiceOld"
    static $inject = ['$http',
        '$q',
        'gpLocalStorageService',
        '$timeout',
        'gpLogglyService',
        AuthorisationCheckerService.serviceName,];

    constructor(public $http: ng.IHttpService,
        public $q: ng.IQService,
        public storage: IStorageService,
        public $timeout: ng.ITimeoutService,
        public logService: ILogglyService,
        private authorisationCheckerService: AuthorisationCheckerService,
    ) {
        this.resetCheckedStatus();
    }

    public authorisationChanged(callback: AuthoisationStatusCallback) {
        this.listeners.push(callback);
        callback(this.lastAuthorisationStatus);
    }

    private onAuthorisationChanged(authStatus: IAuthorisationStatus) {
        this.listeners.forEach(l => l(authStatus));
    }

    public resetCheckedStatus() {
        this.checkAuthPromise = null;
        let authData = this.loadAuthorisationData();

        this.lastAuthorisationStatus = {
            email: authData.email,
            token: authData.token,
            userId: authData.userId,
            loginChecking: false,
            loggedInStatus: 'notsure',
            isAuthorised: !!authData.userId,
        };

        this.onAuthorisationChanged(this.lastAuthorisationStatus);
    }

    public getAuthorisationStatus(): IAuthorisationStatus {
        return this.lastAuthorisationStatus;
    }

    private loadAuthorisationData(): AuthData {

        let authData = this.storage.getAsObject<{ email: string, token: string }>('authorizationData');
        if (authData) {
            const jwt = parseJwtToken(authData.token);
            return {
                email: authData.email,
                token: authData.token,
                userId: (jwt ? jwt.payload.nameid : "") || "",
            }
        } else {
            return {
                email: "",
                token: "",
                userId: "",
            }
        }
    }

    /**
     * The page's own answer to the first authorization check, or null to ask
     * the server as before (issue #1034).
     *
     * ONE-SHOT, and spent on the first call whatever it returns. The fifteen-
     * second `resetCheckedStatus()` loop in app-workspace/app.ts — and the
     * per-view one in app-home — must go back to the network, or a session
     * that expires or a licence that changes while the tab is open stops being
     * noticed.
     *
     * Deliberately does NOT write the token cookie, which the network path
     * does on success. The cookie is what the server just validated to produce
     * this seed; overwriting it here with the localStorage token would put a
     * value the server has not seen in its place.
     */
    private takeRenderedAuthSeed(authData: AuthData): IAuthorisationStatus | null {
        if (this.renderedAuthSeedSpent) {
            return null;
        }

        this.renderedAuthSeedSpent = true;

        const rendered = typeof __gameplan_auth !== 'undefined' ? __gameplan_auth : undefined;
        const jwt = parseJwtToken(authData.token);

        const applies = gpRenderedAuthSeedApplies(
            rendered,
            authData.userId,
            jwt ? jwt.payload.exp : undefined,
            Date.now() / 1000);

        if (!applies) {
            return null;
        }

        return {
            email: authData.email,
            token: authData.token,
            userId: authData.userId,
            loggedInStatus: 'authorized',
            loginChecking: false,
            isAuthorised: true,
        };
    }

    private checkAuthorisationNowWithoutNotify(): ng.IPromise<IAuthorisationStatus> {
        let authData = this.loadAuthorisationData();
        this.lastAuthorisationStatus.loginChecking = true;

        var checkPromise = this.authorisationCheckerService.checkAuthorisation(authData)
            .then(status => {
                var result: IAuthorisationStatus = {
                    email: status.email,
                    token: status.token,
                    userId: status.userId,
                    loggedInStatus: status.userId ? status.loggedInStatus : 'unauthorized',
                    loginChecking: false,
                    isAuthorised: !!status.userId && status.loggedInStatus !== 'unauthorized',

                };
                setGameplanTokenCookie(status.token);
                this.lastAuthorisationStatus = result;
                return result;
            })
            .catch(reason => {
                throw reason;
            })

        return checkPromise;
    }

    public checkAuthorisationIfNeeded(): ng.IPromise<IAuthorisationStatus> {
        if (!this.checkAuthPromise) {

            let seeded = this.takeRenderedAuthSeed(this.loadAuthorisationData());

            if (seeded) {
                // Resolved rather than returned directly, so listeners are
                // notified on a digest exactly as the network path notifies
                // them — callers of this method see no ordering difference,
                // only the absent round trip.
                this.checkAuthPromise = this.$q.when(seeded);
                this.checkAuthPromise.then(status => {
                    this.lastAuthorisationStatus = status;
                    this.onAuthorisationChanged(status);
                });

                return this.checkAuthPromise;
            }

            let delayed = this.$q.defer<IAuthorisationStatus>();

            let authChecker = this.checkAuthorisationNowWithoutNotify();

            let newCheckAuthPromise: ng.IPromise<IAuthorisationStatus>;

            if (this.DEBUG_DELAY > 0) {
                authChecker.then(status => this.$timeout(() => delayed.resolve(status), 2000)).catch(err => { });
                authChecker.catch(reason => this.$timeout(() => delayed.reject(reason), 2000));

                newCheckAuthPromise = delayed.promise;
            } else {
                newCheckAuthPromise = authChecker;
            }

            newCheckAuthPromise.then(authStatus => {
                this.onAuthorisationChanged(authStatus);
            }).catch(err => { });

            newCheckAuthPromise.catch(reason => {
            });

            this.checkAuthPromise = newCheckAuthPromise;

        }

        return this.checkAuthPromise;
    }

    private lastAuthLogTime: dayjs.Dayjs;

}

export const IAuthorisationCheckerService_serviceName = AuthorisationCheckerWithCachingService.serviceName;
gpCommon.service(AuthorisationCheckerWithCachingService.serviceName, AuthorisationCheckerWithCachingService);

export function setGameplanTokenCookie(token: string) {
    for (const write of tokenCookieWrites(token, window.location)) {
        document.cookie = write;
    }
}

